import React, { useEffect, useRef, useState } from "react";
import Wordmark from "./components/Wordmark";
import { APP_VERSION } from "./version";

// ── Laskeutumissivu ───────────────────────────────────────────
//
// Tumma tausta ja neonkeltainen korostus; puhelinkehys ja kortit käyttävät
// Kenttä-ilmeen värejä (docs/ulkoasu.md). Sivu näyttää sovelluksen ennen kuin siihen
// kirjaudutaan: puhelinkehyksessä on sama kenttäkortti ja liigakoroke kuin
// pelaajan näkymässä, ei pelkkää tekstiä. Kolme korttia kantaa viestin
// (Americano, Liiga, DUPR); muut ominaisuudet ovat yhdellä rivillä.

const K = {
  court: "#1d6fb8", courtDeep: "#123f6c", kitchen: "#2f8f6b", ball: "#e9c92a",
  ink: "#0c1c2b", text: "#13293d", muted: "#5f7388", line: "#d8e2ea", soft: "#e6eef5",
  neon: "#d9ff3a", neonInk: "#131a05",
};
const F = { heading: "'Rajdhani', sans-serif", body: "'Sora', sans-serif" };

const STORE = {
  ios: "https://apps.apple.com/fi/app/spinloop-pickleball-events/id6805334882",
  android: "https://play.google.com/store/apps/details?id=app.spinloop",
};

// Teksti- ja ilmekonstantit ovat jaettuja: kirjautumisruutu (UKK-chat) ja
// sovelluksen ensikäynnistyksen esittely käyttävät samoja.
const T = {
  fi: {
    eyebrow: "Pickleball · Americano · Liiga · DUPR",
    h1a: "Pelivuoro turnaukseksi",
    h1b: "yhdellä napilla.",
    sub: "Spinloop arpoo parit, laskee pisteet ja pitää kauden taulukkoa. Klubille ja kaveriporukalle, ilman tilejä ja salasanoja.",
    open: "Avaa sovellus",
    trust: ["Ei salasanoja", "MobilePay", "Toimii selaimessa, iOS ja Android"],
    features: [
      { title: "Americano ja round robin", desc: "Luo tapahtuma, lisää pelaajat ja anna sovelluksen arpoa kierrokset. Tulokset ja tilanne päivittyvät kaikkien puhelimiin heti." },
      { title: "Liiga", desc: "Viikoittaiset illat kerryttävät kauden taulukkoa. Parhaat illat lasketaan, poissaolo ei pudota pelistä, ja jokainen näkee oman sijoituksensa." },
      { title: "DUPR-rating", desc: "Ottelut lähtevät suoraan DUPR:iin, ja virallinen taso kehittyy pelatessa. Pelaaja yhdistää tilinsä itse, yhdellä kirjautumisella." },
    ],
    howTitle: "Näin se toimii",
    how: [
      { n: "1", t: "Järjestäjä luo tapahtuman", d: "Paikka listasta, aika, panos. Liigalle koko kausi kerralla." },
      { n: "2", t: "Pelaajat ilmoittautuvat", d: "Klubikoodilla ja PIN-koodilla. Toisella kerralla sormenjäljellä." },
      { n: "3", t: "Pelataan ja kirjataan", d: "Parit arvotaan, tulokset kirjataan, taulukko elää." },
    ],
    faqTitle: "Kysy Spinloopilta",
    faqSub: "Napauta kysymystä, niin Spinloop vastaa.",
    faqOnline: "vastaa heti",
    faqPick: "Valitse kysymys",
    faqReset: "Aloita alusta",
    faq: [
      { q: "Maksaako Spinloop jotain?", a: "Ei. Spinloop on maksuton pelaajille ja klubeille, ja kaikki ominaisuudet ovat käytössä." },
      { q: "Tarvitsenko tunnuksen tai salasanan?", a: "Et. Kirjaudut klubikoodilla ja tapahtuman PIN-koodilla. Seuraavalla kerralla pääset sisään sormenjäljellä tai Face ID:llä." },
      { q: "Mistä saan klubikoodin ja PIN-koodin?", a: "Järjestäjältä. Ne tulevat yleensä kutsusähköpostissa ennen tapahtumaa." },
      { q: "Voiko Spinloopia käyttää ilman klubia?", a: "Voi. Valitse kirjautumissivulta \"Luo kaveriporukka-tapahtuma\", niin saat koodin ja PIN-koodin, jotka jaat kavereille." },
      { q: "Miten saan oman klubin Spinloopiin?", a: "Täytä klubisivun lomake, niin klubi avataan sinulle.", link: { href: "/clubs", label: "Klubeille →" } },
      { q: "Miten liigan pisteet lasketaan?", a: "Jokaisesta illasta saa sijoituspisteet ja osallistumispisteen. Kauden taulukkoon lasketaan parhaat illat, joten yksi poissaolo ei pudota pelistä. Klubi päättää tarkat säännöt, ja ne näkyvät jokaisen liigaillan sivulla." },
      { q: "Miten DUPR-tili yhdistetään?", a: "Profiilissa on nappi \"Yhdistä DUPR-tili\". Kirjaudut DUPR:iin ja hyväksyt yhdistämisen. Kun järjestäjä lähettää illan tulokset, ne päivittyvät DUPR-tasoosi." },
      { q: "Mitä tietoja minusta tallennetaan?", a: "Nimesi, sähköpostisi kutsuja varten ja ottelutuloksesi. Tarkemmin tietosuojaselosteessa.", link: { href: "/privacy", label: "Tietosuoja →" } },
    ],
    installTitle: "Asenna puhelimeen",
    installIos: "iPhone: App Store, tai Safari → Jaa → Lisää Koti-valikkoon",
    installAndroid: "Android: Google Play, tai Chrome → Valikko → Lisää aloitusnäyttöön",
    privacy: "Tietosuoja",
    phone: { club: "Kenttäkerho", venue: "Pickleball Areena 1", date: "lauantai 10. lokakuuta", in: "Olet mukana", season: "Talvi 2026", night: "ilta 3 / 12", podium: "Kauden kärki" },
  },
  en: {
    eyebrow: "Pickleball · Americano · League · DUPR",
    h1a: "From court time to a tournament",
    h1b: "in one tap.",
    sub: "Spinloop draws the pairs, keeps score and runs the season table. For clubs and friend groups, with no accounts or passwords.",
    open: "Open app",
    trust: ["No passwords", "MobilePay", "Works in the browser, iOS and Android"],
    features: [
      { title: "Americano & round robin", desc: "Create an event, add players and let the app draw the rounds. Scores and standings update on everyone's phone instantly." },
      { title: "League", desc: "Weekly nights build a season table. Best nights count, missing one doesn't drop you out, and everyone sees their own rank." },
      { title: "DUPR rating", desc: "Matches go straight to DUPR and your official rating grows as you play. Players connect their own account with one sign-in." },
    ],
    howTitle: "How it works",
    how: [
      { n: "1", t: "The organizer creates an event", d: "Venue from a list, time, entry fee. For a league, the whole season at once." },
      { n: "2", t: "Players sign up", d: "With a club code and a PIN. Next time with a fingerprint." },
      { n: "3", t: "Play and record", d: "Pairs are drawn, results entered, the table comes alive." },
    ],
    faqTitle: "Ask Spinloop",
    faqSub: "Tap a question and Spinloop answers.",
    faqOnline: "replies instantly",
    faqPick: "Pick a question",
    faqReset: "Start over",
    faq: [
      { q: "Does Spinloop cost anything?", a: "No. Spinloop is free for players and clubs, with every feature included." },
      { q: "Do I need an account or a password?", a: "No. You sign in with a club code and the event PIN. Next time you can use your fingerprint or Face ID." },
      { q: "Where do I get the club code and PIN?", a: "From the organizer. They usually come in the invitation email before the event." },
      { q: "Can I use Spinloop without a club?", a: "Yes. Choose \"Create a friend group event\" on the sign-in page and you get a code and a PIN to share with your friends." },
      { q: "How do I get my club on Spinloop?", a: "Fill in the form on the clubs page and we'll set up your club.", link: { href: "/clubs", label: "For clubs →" } },
      { q: "How are league points counted?", a: "Each night gives placement points plus a point for showing up. Only your best nights count for the season, so missing one doesn't drop you out. The club sets the exact rules, and they are shown on every league night." },
      { q: "How do I connect my DUPR account?", a: "Your profile has a \"Connect DUPR Account\" button. You sign in to DUPR and approve the connection. When the organizer submits the night's results, they update your DUPR rating." },
      { q: "What data do you store about me?", a: "Your name, your email for invitations, and your match results. See the privacy policy for details.", link: { href: "/privacy", label: "Privacy →" } },
    ],
    installTitle: "Install on your phone",
    installIos: "iPhone: App Store, or Safari → Share → Add to Home Screen",
    installAndroid: "Android: Google Play, or Chrome → Menu → Add to Home Screen",
    privacy: "Privacy Policy",
    phone: { club: "Kenttäkerho", venue: "Pickleball Areena 1", date: "Saturday 10 October", in: "You're in", season: "Winter 2026", night: "night 3 / 12", podium: "Season top 3" },
  },
};

// Korttien kuvitukset ja pienet kuvakkeet ovat kuvatiedostoja (public/icons/feature-*.png).
export const FEATURE_ART = ["/icons/feature-art-americano.png", "/icons/feature-art-liiga.png", "/icons/feature-art-dupr.png"];
const FEATURE_ICONS = ["/icons/feature-americano.png", "/icons/feature-liiga.png", "/icons/feature-dupr.png"];

type FaqItem = { q: string; a: string; link?: { href: string; label: string } };
type FaqText = { faq: FaqItem[]; faqOnline: string; faqPick: string; faqReset: string };

// UKK keskusteluna: kysymykset ovat pikavastausnappeja, vastaus tulee kuplana
// lyhyen "kirjoittaa..."-hetken jälkeen. Ensimmäinen kysymys on valmiiksi
// auki, ettei ikkuna ole tyhjä.
export function FaqChat({ t }: { t: FaqText }) {
  const [thread, setThread] = useState<{ i: number; answered: boolean }[]>([{ i: 0, answered: true }]);
  const logRef = useRef<HTMLDivElement>(null);
  const timers = useRef<number[]>([]);
  const busy = thread.some(m => !m.answered);

  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [thread]);
  useEffect(() => () => timers.current.forEach(id => window.clearTimeout(id)), []);

  function ask(i: number) {
    if (busy || thread.some(m => m.i === i)) return;
    setThread(prev => [...prev, { i, answered: false }]);
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const id = window.setTimeout(() => {
      setThread(prev => prev.map(m => (m.i === i ? { ...m, answered: true } : m)));
    }, reduced ? 0 : 750);
    timers.current.push(id);
  }

  const bubbleBase: React.CSSProperties = { maxWidth: "82%", padding: "10px 14px", fontSize: 14, lineHeight: 1.5 };
  const remaining = t.faq.map((_, i) => i).filter(i => !thread.some(m => m.i === i));

  return (
    <div style={{ background: "rgba(7,11,18,0.55)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 22, overflow: "hidden", marginBottom: 40, boxShadow: "0 18px 44px rgba(0,0,0,0.45)", backdropFilter: "blur(8px)" }}>
      {/* Otsikkorivi kuin viestisovelluksessa */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)" }}>
        <div style={{ position: "relative" }}>
          <img src="/icons/mark-96.png" alt="" style={{ width: 36, height: 36, borderRadius: 10, display: "block" }} />
          <span style={{ position: "absolute", right: -2, bottom: -2, width: 11, height: 11, borderRadius: "50%", background: K.neon, border: "2px solid #0b1220" }} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Spinloop</div>
          <div style={{ fontSize: 11, color: K.neon }}>{t.faqOnline}</div>
        </div>
        {thread.length > 1 && (
          <button type="button" onClick={() => setThread([{ i: 0, answered: true }])} style={{ marginLeft: "auto", background: "none", border: "1px solid rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.75)", borderRadius: 999, padding: "6px 12px", fontSize: 12, fontFamily: F.body, cursor: "pointer" }}>
            ↺ {t.faqReset}
          </button>
        )}
      </div>

      {/* Keskustelu */}
      <div ref={logRef} role="log" aria-live="polite" style={{ display: "flex", flexDirection: "column", gap: 10, padding: 16, maxHeight: 440, overflowY: "auto" }}>
        {thread.map(m => {
          const item = t.faq[m.i];
          return (
            <React.Fragment key={m.i}>
              <div className="sl-bubble" style={{ ...bubbleBase, alignSelf: "flex-end", background: K.neon, color: K.neonInk, fontWeight: 600, borderRadius: "18px 18px 4px 18px" }}>
                {item.q}
              </div>
              {m.answered ? (
                <div className="sl-bubble" style={{ display: "flex", gap: 8, alignItems: "flex-end", alignSelf: "flex-start", maxWidth: "88%" }}>
                  <img src="/icons/mark-96.png" alt="" style={{ width: 26, height: 26, borderRadius: 8, flexShrink: 0 }} />
                  <div style={{ ...bubbleBase, maxWidth: "none", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.92)", borderRadius: "18px 18px 18px 4px" }}>
                    {item.a}
                    {item.link && (
                      <> <a href={item.link.href} style={{ color: K.neon, fontWeight: 700, textDecoration: "none", whiteSpace: "nowrap" }}>{item.link.label}</a></>
                    )}
                  </div>
                </div>
              ) : (
                <div className="sl-bubble" style={{ display: "flex", gap: 8, alignItems: "flex-end", alignSelf: "flex-start" }} aria-label="…">
                  <img src="/icons/mark-96.png" alt="" style={{ width: 26, height: 26, borderRadius: 8, flexShrink: 0 }} />
                  <div style={{ display: "flex", gap: 4, padding: "13px 14px", background: "rgba(255,255,255,0.1)", borderRadius: "18px 18px 18px 4px" }}>
                    <span className="sl-dot" /><span className="sl-dot" /><span className="sl-dot" />
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Pikavastaukset */}
      {remaining.length > 0 && (
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", padding: "12px 16px 16px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)", marginBottom: 8 }}>{t.faqPick}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {remaining.map(i => (
              <button key={i} type="button" className="sl-chip" disabled={busy} onClick={() => ask(i)} style={{
                background: "rgba(217,255,58,0.06)", border: "1px solid rgba(217,255,58,0.35)", color: "#fff",
                borderRadius: 999, padding: "8px 14px", fontSize: 13, fontFamily: F.body, cursor: busy ? "default" : "pointer",
                opacity: busy ? 0.5 : 1, textAlign: "left",
              }}>
                {t.faq[i].q}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function LandingPage({ onEnter }: { onEnter: () => void }) {
  const [lang, setLang] = useState<"fi"|"en">(() =>
    (localStorage.getItem("spinloop_lang") as "fi"|"en") || "fi"
  );
  function toggleLang() {
    const next = lang === "fi" ? "en" : "fi";
    setLang(next);
    localStorage.setItem("spinloop_lang", next);
  }
  const t = T[lang];

  const primaryBtn: React.CSSProperties = {
    background: K.neon, color: K.neonInk, border: "none", borderRadius: 14,
    padding: "15px 28px", fontSize: 16, fontWeight: 700, fontFamily: F.body, cursor: "pointer",
    boxShadow: "0 0 0 1px rgba(217,255,58,0.5), 0 12px 34px rgba(217,255,58,0.35)", display: "inline-flex", alignItems: "center", gap: 8,
  };
  const storeBtn: React.CSSProperties = {
    display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none",
    background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.22)", color: "#fff",
    borderRadius: 14, padding: "13px 18px", fontSize: 14, fontWeight: 700, fontFamily: F.body,
  };

  return (
    <div style={{
      minHeight: "100vh", fontFamily: F.body, color: "#fff", overflowX: "hidden",
      background: "#070b12",
      // Kenttäkuva näkyy haaleana tumman sinisen alta; hehkut päälle.
      backgroundImage: [
        "radial-gradient(60% 50% at 85% 10%, rgba(29,111,184,0.40) 0%, rgba(29,111,184,0) 70%)",
        "radial-gradient(45% 40% at 10% 40%, rgba(217,255,58,0.10) 0%, rgba(217,255,58,0) 70%)",
        "radial-gradient(50% 40% at 50% 100%, rgba(47,143,107,0.22) 0%, rgba(47,143,107,0) 70%)",
        "linear-gradient(180deg, rgba(7,11,18,0.80) 0%, rgba(9,18,34,0.78) 50%, rgba(7,11,18,0.90) 100%)",
        "url('/court-bg.jpg')",
      ].join(", "),
      backgroundSize: "auto, auto, auto, auto, cover", backgroundPosition: "center", backgroundAttachment: "fixed",
    }}>
      <style>{`
        .sl-hero { display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 40px; align-items: center; }
        .sl-features { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
        .sl-card { display: grid; grid-template-columns: 128px 1fr; gap: 14px; align-items: start; }
        .sl-art { width: 128px; height: 128px; }
        @media (max-width: 1000px) and (min-width: 861px) { .sl-card { grid-template-columns: 1fr; } .sl-art { width: 96px; height: 96px; } }
        .sl-how { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }
        .sl-h1 { font-size: 56px; }
        .sl-chip { transition: background 0.15s ease, border-color 0.15s ease, transform 0.1s ease; }
        .sl-chip:hover:not(:disabled) { background: rgba(217,255,58,0.16); border-color: rgba(217,255,58,0.6); }
        .sl-chip:active:not(:disabled) { transform: scale(0.97); }
        .sl-dot { width: 7px; height: 7px; border-radius: 50%; background: rgba(255,255,255,0.7); display: inline-block; }
        @media (prefers-reduced-motion: no-preference) {
          .sl-bubble { animation: sl-pop 0.22s ease-out; }
          @keyframes sl-pop { from { opacity: 0; transform: translateY(6px) scale(0.98); } to { opacity: 1; transform: none; } }
          .sl-dot { animation: sl-blink 1s infinite ease-in-out; }
          .sl-dot:nth-child(2) { animation-delay: 0.15s; }
          .sl-dot:nth-child(3) { animation-delay: 0.3s; }
          @keyframes sl-blink { 0%, 80%, 100% { opacity: 0.25; transform: translateY(0); } 40% { opacity: 1; transform: translateY(-3px); } }
        }
        @media (max-width: 860px) {
          .sl-hero { grid-template-columns: 1fr; gap: 28px; }
          .sl-features, .sl-how { grid-template-columns: 1fr; }
          .sl-card { grid-template-columns: 104px 1fr; }
          .sl-art { width: 104px; height: 104px; }
          .sl-h1 { font-size: 40px; }
        }
        .sl-phone { transform: perspective(1400px) rotateY(-20deg) rotateX(6deg) rotateZ(-4deg); }
        .sl-phone-wrap { perspective: 1400px; }
        @media (max-width: 860px) {
          .sl-phone { transform: perspective(1400px) rotateY(-12deg) rotateX(4deg) rotateZ(-3deg); }
        }
        @media (prefers-reduced-motion: no-preference) {
          .sl-phone { animation: sl-float 7s ease-in-out infinite; }
          @keyframes sl-float {
            0%, 100% { transform: perspective(1400px) rotateY(-20deg) rotateX(6deg) rotateZ(-4deg) translateY(0); }
            50% { transform: perspective(1400px) rotateY(-16deg) rotateX(4deg) rotateZ(-3deg) translateY(-10px); }
          }
        }
      `}</style>

      {/* Pallo vesileimana oikeassa yläkulmassa */}
      <img src="/icons/pickleball.png" alt="" aria-hidden="true" style={{
        position: "fixed", right: -160, top: -120, width: 520, height: 520, opacity: 0.07,
        transform: "rotate(-14deg)", pointerEvents: "none", zIndex: 0, userSelect: "none",
      }} />

      <div style={{ position: "relative", zIndex: 1, maxWidth: 1040, margin: "0 auto", padding: "0 20px 56px" }}>

        {/* Yläpalkki */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 18, gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/icons/mark-96.png" alt="" style={{ width: 36, height: 36, borderRadius: 10 }} />
            <span style={{ fontFamily: F.heading, fontWeight: 700, fontSize: 22, letterSpacing: 2 }}>SPIN<span style={{ color: K.neon }}>LOOP</span></span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={toggleLang} style={{
              background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.22)",
              borderRadius: 999, padding: "7px 14px", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: F.body,
            }}>{lang === "fi" ? "EN" : "FI"}</button>
            <button onClick={onEnter} style={{ ...primaryBtn, padding: "9px 16px", fontSize: 13, borderRadius: 999, boxShadow: "0 0 18px rgba(217,255,58,0.35)" }}>{t.open} →</button>
          </div>
        </div>

        {/* Hero */}
        <div className="sl-hero" style={{ paddingTop: 48, paddingBottom: 48 }}>
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: K.neon, background: "rgba(217,255,58,0.08)", border: "1px solid rgba(217,255,58,0.45)", borderRadius: 999, padding: "6px 12px" }}>
              {t.eyebrow}
            </div>
            <h1 className="sl-h1" style={{ fontFamily: F.heading, fontWeight: 700, lineHeight: 0.98, letterSpacing: "0.01em", margin: "18px 0 14px", textWrap: "balance" as React.CSSProperties["textWrap"] }}>
              {t.h1a}<br /><span style={{ color: K.neon, textShadow: "0 0 24px rgba(217,255,58,0.45)" }}>{t.h1b}</span>
            </h1>
            <p style={{ fontSize: 17, lineHeight: 1.6, color: "rgba(255,255,255,0.82)", margin: "0 0 26px", maxWidth: 520 }}>{t.sub}</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
              <button onClick={onEnter} style={primaryBtn}
                onMouseDown={e => (e.currentTarget.style.transform = "scale(0.97)")}
                onMouseUp={e => (e.currentTarget.style.transform = "scale(1)")}
                onTouchStart={e => (e.currentTarget.style.transform = "scale(0.97)")}
                onTouchEnd={e => (e.currentTarget.style.transform = "scale(1)")}>
                {t.open} <span aria-hidden="true">→</span>
              </button>
              <a href={STORE.ios} target="_blank" rel="noreferrer" style={storeBtn}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16.4 12.6c0-2.4 2-3.6 2.1-3.7-1.1-1.7-2.9-1.9-3.5-1.9-1.5-.2-2.9.9-3.7.9-.8 0-1.9-.9-3.2-.8-1.6 0-3.1 1-4 2.4-1.7 3-.4 7.3 1.2 9.7.8 1.2 1.8 2.5 3.1 2.4 1.2 0 1.7-.8 3.2-.8s1.9.8 3.2.8c1.3 0 2.2-1.2 3-2.4.9-1.4 1.3-2.7 1.3-2.8 0 0-2.6-1-2.7-3.8zM14 5.5c.7-.8 1.1-2 1-3.1-1 0-2.2.7-2.9 1.5-.6.7-1.2 1.9-1 3 1.1.1 2.2-.6 2.9-1.4z"></path></svg>
                App Store
              </a>
              <a href={STORE.android} target="_blank" rel="noreferrer" style={storeBtn}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3.6 2.5 13.3 12l-9.7 9.5c-.3-.2-.4-.6-.4-1V3.5c0-.4.1-.8.4-1zm11.1 10.9 2.6 2.6-9.8 5.6c-.6.3-1.2.4-1.7.2l8.9-8.4zm0-2.8L5.8 2.2c.5-.2 1.1-.1 1.7.2l9.8 5.6-2.6 2.6zm4.2 1.4c.8.5.8 1.5 0 2l-2.7 1.5-2.9-2.5 2.9-2.5 2.7 1.5z"></path></svg>
                Google Play
              </a>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", marginTop: 18, fontSize: 12, color: "rgba(255,255,255,0.6)" }}>
              {t.trust.map(x => <span key={x}><span style={{ color: K.neon }}>✓</span> {x}</span>)}
            </div>
          </div>

          {/* Puhelin: sama kenttäkortti ja koroke kuin sovelluksessa */}
          <div className="sl-phone-wrap" style={{ display: "flex", justifyContent: "center" }}>
            <div className="sl-phone" style={{ width: 300, maxWidth: "100%", borderRadius: 38, padding: 10, background: "#0b0f14", boxShadow: "0 40px 80px rgba(0,0,0,0.6), 0 0 60px rgba(29,111,184,0.25)" }}>
              <div style={{ borderRadius: 30, overflow: "hidden", background: "#eef3f7", color: K.text, fontFamily: F.body }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "22px 14px 10px", background: "#fff", borderBottom: `1px solid ${K.line}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <img src="/icons/mark-96.png" alt="" style={{ width: 30, height: 30, borderRadius: 8 }} />
                    <div>
                      <div style={{ fontFamily: F.heading, fontWeight: 700, fontSize: 16, letterSpacing: 1.5, lineHeight: 1, color: K.court }}><Wordmark /></div>
                      <div style={{ fontSize: 9, color: K.muted }}>{t.phone.club} · Kari H.</div>
                    </div>
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "6px 10px", borderRadius: 999, background: K.soft, color: K.court }}>{lang === "fi" ? "Ulos" : "Out"}</span>
                </div>
                <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ position: "relative", overflow: "hidden", borderRadius: 16, padding: "14px 12px 12px", color: "#fff", background: K.court, boxShadow: "0 10px 24px rgba(29,111,184,0.35)" }}>
                    <div style={{ position: "absolute", left: 0, right: 0, top: "58%", height: "22%", background: K.kitchen, opacity: 0.45 }} />
                    <div style={{ position: "absolute", left: 0, right: 0, top: "80%", borderTop: "2px dashed rgba(255,255,255,0.6)" }} />
                    <div style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, rgba(8,28,52,0.42) 0%, rgba(8,28,52,0.18) 55%, transparent 75%)" }} />
                    <img src="/icons/pickleball.png" alt="" style={{ position: "absolute", right: -14, top: -14, width: 96, height: 96, transform: "rotate(-12deg)", filter: "drop-shadow(0 10px 14px rgba(0,0,0,0.35))" }} />
                    <div style={{ position: "relative", display: "flex", flexDirection: "column", gap: 3 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, paddingRight: 70 }}>
                        <span style={{ fontSize: 8, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700, opacity: 0.85 }}>Pickleball Americano</span>
                        <span style={{ fontSize: 8, fontWeight: 700, padding: "2px 6px", borderRadius: 999, background: K.kitchen }}>🏆 {t.phone.season}</span>
                      </div>
                      <div style={{ fontFamily: F.heading, fontWeight: 700, fontSize: 22, lineHeight: 1, paddingRight: 70, textShadow: "0 1px 2px rgba(0,0,0,0.35)" }}>{t.phone.venue}</div>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 3, textShadow: "0 1px 2px rgba(0,0,0,0.35)" }}>
                        <span style={{ fontFamily: F.heading, fontWeight: 700, fontSize: 18, lineHeight: 1, color: K.ball }}>15:00</span>
                        <span style={{ fontSize: 11, fontWeight: 700 }}>{t.phone.date}</span>
                      </div>
                      <span style={{ alignSelf: "flex-start", marginTop: 8, fontSize: 10, fontWeight: 700, padding: "5px 10px", borderRadius: 999, background: K.kitchen }}>✓ {t.phone.in}</span>
                    </div>
                  </div>
                  <div style={{ background: "#fff", border: `1px solid ${K.line}`, borderRadius: 12, padding: "8px 10px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                      <span style={{ fontFamily: F.heading, fontWeight: 700, fontSize: 12, color: K.court }}>🏆 {t.phone.podium}</span>
                      <span style={{ fontSize: 9, color: K.muted }}>{t.phone.night}</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 6, alignItems: "end" }}>
                      {[["Kari H.", 64, 2], ["Matti V.", 67, 1], ["Sauli K.", 58, 3]].map(([n, p, r]) => (
                        <div key={String(n)} style={{ textAlign: "center", background: r === 1 ? "#fff" : K.soft, border: r === 1 ? `2px solid ${K.ball}` : `1px solid ${K.line}`, borderRadius: 10, padding: r === 1 ? "8px 4px 6px" : "6px 4px 5px", boxShadow: r === 1 ? "0 8px 18px rgba(233,201,42,0.35)" : undefined }}>
                          <div style={{ width: r === 1 ? 26 : 22, height: r === 1 ? 26 : 22, borderRadius: "50%", margin: "0 auto 3px", background: r === 1 ? K.court : K.ball, color: r === 1 ? "#fff" : K.text, fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{String(n).charAt(0)}</div>
                          <div style={{ fontSize: 9, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{n}</div>
                          <div style={{ fontFamily: F.heading, fontWeight: 700, fontSize: r === 1 ? 18 : 14, lineHeight: 1, color: K.court }}>{p}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", padding: "6px 6px 12px", background: "#fff", borderTop: `1px solid ${K.line}` }}>
                  {[["📅", lang === "fi" ? "Tapahtumat" : "Events", true], ["📊", lang === "fi" ? "Liiga" : "League", false], ["📰", lang === "fi" ? "Uutiset" : "News", false], ["👤", lang === "fi" ? "Profiili" : "Profile", false]].map(([i, l, on]) => (
                    <div key={String(l)} style={{ flex: 1, margin: "0 2px", padding: "5px 0 4px", borderRadius: 10, background: on ? K.soft : "none", textAlign: "center", fontSize: 8, fontWeight: 700, color: on ? K.court : K.muted }}>
                      <div style={{ fontSize: 14 }}>{i}</div>{l}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Kolme korttia: iso kuvitus vasemmalla, teksti oikealla */}
        <div className="sl-features" style={{ marginBottom: 40 }}>
          {t.features.map((f, i) => {
            const tone = [K.court, K.kitchen, K.ball][i];
            return (
              <div key={f.title} className="sl-card" style={{
                background: "linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03))",
                border: `1.5px solid ${tone}`, borderRadius: 22, padding: 16,
                boxShadow: `0 0 0 1px rgba(0,0,0,0.3), 0 18px 44px rgba(0,0,0,0.45), 0 0 36px ${tone}33`,
                backdropFilter: "blur(6px)",
              }}>
                <img src={FEATURE_ART[i]} alt="" className="sl-art" style={{ borderRadius: 18, display: "block", boxShadow: `0 12px 26px ${tone}55` }} />
                <div style={{ minWidth: 0 }}>
                  <img src={FEATURE_ICONS[i]} alt="" style={{ width: 34, height: 34, borderRadius: 9, display: "block", marginBottom: 8 }} />
                  <div style={{ fontFamily: F.heading, fontWeight: 700, fontSize: 21, letterSpacing: "0.02em", lineHeight: 1.05, marginBottom: 6, color: "#fff" }}>{f.title}</div>
                  <div style={{ fontSize: 13, lineHeight: 1.6, color: "rgba(255,255,255,0.72)" }}>{f.desc}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Näin se toimii */}
        <div style={{ fontFamily: F.heading, fontWeight: 700, fontSize: 22, letterSpacing: "0.06em", textTransform: "uppercase", color: "rgba(255,255,255,0.7)", marginBottom: 12 }}>{t.howTitle}</div>
        <div className="sl-how" style={{ marginBottom: 40 }}>
          {t.how.map(s => (
            <div key={s.n} style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 16, padding: "16px 16px" }}>
              <div style={{ fontFamily: F.heading, fontWeight: 700, fontSize: 28, lineHeight: 1, color: K.neon, flexShrink: 0, textShadow: "0 0 14px rgba(217,255,58,0.5)" }}>{s.n}</div>
              <div><div style={{ fontWeight: 700, fontSize: 14, marginBottom: 3 }}>{s.t}</div><div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", lineHeight: 1.5 }}>{s.d}</div></div>
            </div>
          ))}
        </div>

        {/* Kysy Spinloopilta: UKK chat-keskusteluna */}
        <div style={{ fontFamily: F.heading, fontWeight: 700, fontSize: 22, letterSpacing: "0.06em", textTransform: "uppercase", color: "rgba(255,255,255,0.7)", marginBottom: 4 }}>{t.faqTitle}</div>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", marginBottom: 14 }}>{t.faqSub}</div>
        <FaqChat t={t} />

        {/* Asennus */}
        <div style={{ background: "rgba(217,255,58,0.07)", border: "1px solid rgba(217,255,58,0.3)", borderRadius: 16, padding: "18px 20px", marginBottom: 28, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: K.neon, marginBottom: 4 }}>📱 {t.installTitle}</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", lineHeight: 1.6 }}>{t.installIos}<br />{t.installAndroid}</div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <a href={STORE.ios} target="_blank" rel="noreferrer" style={{ ...storeBtn, padding: "10px 14px", fontSize: 13 }}>App Store</a>
            <a href={STORE.android} target="_blank" rel="noreferrer" style={{ ...storeBtn, padding: "10px 14px", fontSize: 13 }}>Google Play</a>
          </div>
        </div>

        {/* Alatunniste */}
        <div style={{ textAlign: "center", fontSize: 11, color: "rgba(255,255,255,0.4)", lineHeight: 1.8 }}>
          DeepEnd Solutions Oy
          <span style={{ margin: "0 8px" }}>·</span>
          info@spinloop.app · spinloop.app
          <span style={{ margin: "0 8px" }}>·</span>
          <a href="/privacy" style={{ color: "rgba(255,255,255,0.55)", textDecoration: "underline" }}>{t.privacy}</a>
          <span style={{ margin: "0 8px" }}>·</span>
          {/* Versio näkyvissä, jotta päivityksen perillemeno on helppo todeta */}
          <span>v{APP_VERSION}</span>
        </div>
      </div>
    </div>
  );
}

export { T as LANDING_TEXT, K as LANDING_COLORS };
